import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Shield, Plus, Edit, Trash2, Eye, CheckCircle, MessageSquare, Users, FileText, Upload, Link as LinkIcon, Bell } from "lucide-react";
import { getAuthenticatedFileUrl } from '../utils/fileUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Informative({ user }) {
  const [comunicazioni, setComunicazioni] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [activePolicy, setActivePolicy] = useState(null);
  const [consents, setConsents] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [statisticheLettureGlobali, setStatisticheLettureGlobali] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  
  // Filtri
  const [filtroTipologia, setFiltroTipologia] = useState('');
  const [filtroDataDa, setFiltroDataDa] = useState('');
  const [filtroDataA, setFiltroDataA] = useState('');
  
  const [showComunicazioneDialog, setShowComunicazioneDialog] = useState(false);
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [showViewComunicazioneDialog, setShowViewComunicazioneDialog] = useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showEditPolicyDialog, setShowEditPolicyDialog] = useState(false);
  const [showViewPolicyDialog, setShowViewPolicyDialog] = useState(false);
  const [showTipologieDialog, setShowTipologieDialog] = useState(false);
  const [showLettureDialog, setShowLettureDialog] = useState(false);
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [lettureData, setLettureData] = useState(null);
  
  const [comunicazioneForm, setComunicazioneForm] = useState({
    tipo: 'comunicazione',
    tipologia_id: '',
    titolo: '',
    contenuto: '',
    link_esterno: '',
    ruoli_abilitati: [],
    profili_abilitati: [],
    asl_abilitate: [],
    combinazione_filtri: 'OR',
    invia_notifiche: false  // Default false per sicurezza
  });
  
  const [pdfFile, setPdfFile] = useState(null);
  
  const [tipologiaForm, setTipologiaForm] = useState({
    nome: '',
    emoji: '📣',
    colore: '#3B82F6',
    ordine: 0
  });
  
  const [policyForm, setPolicyForm] = useState({
    version: '',
    title: 'Informativa Privacy',
    content: '',
    effective_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const comRes = await axios.get(`${API}/comunicazioni/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComunicazioni(comRes.data);
      
      const policiesRes = await axios.get(`${API}/privacy-policy`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(policiesRes.data);
      
      const active = policiesRes.data.find(p => p.is_active);
      setActivePolicy(active);
      
      const consentsRes = await axios.get(`${API}/privacy-consents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsents(consentsRes.data);
      
      const tipologieRes = await axios.get(`${API}/tipologie-comunicazioni`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTipologie(tipologieRes.data);
      
      // Statistiche globali
      const statsRes = await axios.get(`${API}/comunicazioni/statistiche-globali`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatisticheLettureGlobali(statsRes.data);
      
    } catch (err) {
      console.error('Errore caricamento:', err);
      toast.error('Errore caricamento dati');
    }
  };
  
  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/public`);
      setSettings(res.data);
    } catch (err) {
      console.error('Errore settings:', err);
    }
  };
  
  const fetchLetturePolicy = async (policyId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/privacy-policy/${policyId}/letture`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLettureData(res.data);
      setShowLettureDialog(true);
    } catch (err) {
      console.error('Errore caricamento letture:', err);
      toast.error('Errore nel caricamento delle letture');
    }
  };

  const handleSubmitComunicazione = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (selectedItem) {
        await axios.put(`${API}/comunicazioni/${selectedItem.id}`, comunicazioneForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const res = await axios.post(`${API}/comunicazioni`, comunicazioneForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (pdfFile) {
          const formData = new FormData();
          formData.append('file', pdfFile);
          
          await axios.post(`${API}/comunicazioni/${res.data.id}/upload-pdf`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }
      
      toast.success(selectedItem ? 'Aggiornata' : 'Creata');
      setShowComunicazioneDialog(false);
      resetComunicazioneForm();
      fetchData();
    } catch (err) {
      console.error('Errore:', err);
      toast.error('Errore operazione');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteComunicazione = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/comunicazioni/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Eliminata');
      fetchData();
    } catch (err) {
      toast.error('Errore eliminazione');
    }
  };
  
  const openTrackingDialog = async (com) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/comunicazioni/${com.id}/letture`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTrackingData(res.data);
      setSelectedItem(com);
      setShowTrackingDialog(true);
    } catch (err) {
      toast.error('Errore tracking');
    }
  };
  
  const resetComunicazioneForm = () => {
    setComunicazioneForm({
      tipo: 'comunicazione',
      tipologia_id: '',
      titolo: '',
      contenuto: '',
      link_esterno: '',
      ruoli_abilitati: [],
      profili_abilitati: [],
      asl_abilitate: [],
      combinazione_filtri: 'OR',
      invia_notifiche: false  // Default false per sicurezza
    });
    setPdfFile(null);
    setSelectedItem(null);
  };

  const handleSubmitPolicy = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (showEditPolicyDialog) {
        await axios.put(`${API}/privacy-policy/${selectedItem.id}`, {
          title: policyForm.title,
          content: policyForm.content
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Policy aggiornata');
        setShowEditPolicyDialog(false);
      } else {
        await axios.post(`${API}/privacy-policy`, policyForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Policy creata');
        setShowPolicyDialog(false);
      }
      
      setPolicyForm({
        version: '',
        title: 'Informativa Privacy',
        content: '',
        effective_date: new Date().toISOString().split('T')[0]
      });
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePolicy = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/privacy-policy/${id}`, 
        { is_active: true },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Attivata');
      fetchData();
    } catch (err) {
      toast.error('Errore attivazione');
    }
  };

  const handleDeletePolicy = async (id) => {
    if (!window.confirm('Eliminare policy?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/privacy-policy/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Eliminata');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleCreateTipologia = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tipologie-comunicazioni`, tipologiaForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tipologia creata');
      setTipologiaForm({ nome: '', emoji: '📣', colore: '#3B82F6', ordine: 0 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTipologia = async (id) => {
    if (!window.confirm('Eliminare tipologia?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tipologie-comunicazioni/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Eliminata');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const toggleArray = (array, value, setter) => {
    if (array.includes(value)) {
      setter(array.filter(v => v !== value));
    } else {
      setter([...array, value]);
    }
  };

  const getComunicazioniFiltrate = () => {
    return comunicazioni.filter(com => {
      // Filtro tipologia
      if (filtroTipologia && com.tipologia_id !== filtroTipologia) {
        return false;
      }
      
      // Filtro data
      const comData = new Date(com.created_at);
      if (filtroDataDa) {
        const dataDa = new Date(filtroDataDa);
        if (comData < dataDa) return false;
      }
      if (filtroDataA) {
        const dataA = new Date(filtroDataA);
        dataA.setHours(23, 59, 59, 999);
        if (comData > dataA) return false;
      }
      
      return true;
    });
  };

  const esportaPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const comunicazioniFiltrate = getComunicazioniFiltrate();
      
      // Prepara i dati per il backend
      const payload = {
        comunicazioni: comunicazioniFiltrate.map(com => ({
          protocollo: com.protocollo,
          tipologia_emoji: com.tipologia_emoji,
          tipologia_nome: com.tipologia_nome,
          titolo: com.titolo,
          contenuto: com.contenuto,
          data_creazione: new Date(com.created_at).toISOString(),
          letture: com.letture || [],
          totale_letture: com.letture?.length || 0
        }))
      };
      
      const response = await axios.post(`${API}/comunicazioni/export-pdf`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });
      
      // Download del file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comunicazioni_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF scaricato');
    } catch (err) {
      console.error('Errore export PDF:', err);
      toast.error('Errore export PDF');
    }
  };

  const esportaExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const comunicazioniFiltrate = getComunicazioniFiltrate();
      
      // Prepara i dati per il backend
      const payload = {
        comunicazioni: comunicazioniFiltrate.map(com => ({
          protocollo: com.protocollo,
          tipologia_emoji: com.tipologia_emoji,
          tipologia_nome: com.tipologia_nome,
          titolo: com.titolo,
          contenuto: com.contenuto,
          data_creazione: new Date(com.created_at).toISOString(),
          letture: com.letture || [],
          totale_letture: com.letture?.length || 0,
          ruoli: com.ruoli_abilitati?.join(', ') || 'Tutti',
          profili: com.profili_abilitati?.join(', ') || 'Tutti',
          asl: com.asl_abilitate?.join(', ') || 'Tutte'
        }))
      };
      
      const response = await axios.post(`${API}/comunicazioni/export-excel`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });
      
      // Download del file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comunicazioni_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel scaricato');
    } catch (err) {
      console.error('Errore export Excel:', err);
      toast.error('Errore export Excel');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-600" />
            Informative
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Gestisci comunicazioni operative e informative privacy
          </p>
        </div>
      </div>

      {/* SEZIONE COMUNICAZIONI */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="w-4 h-4 text-cyan-600" />
                Comunicazioni & Note Operative
              </CardTitle>
              <CardDescription className="text-xs">
                Pubblica comunicazioni con tracking letture
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setShowTipologieDialog(true)}
                variant="outline" size="sm"
                className="h-8 gap-1.5 text-xs border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <Edit className="w-3.5 h-3.5" />
                Tipologie
              </Button>
              <Button 
                onClick={() => {
                  resetComunicazioneForm();
                  setShowComunicazioneDialog(true);
                }}
                size="sm"
                className="h-8 gap-1.5 text-xs bg-cyan-600 hover:bg-cyan-700"
              >
                <Plus className="w-3.5 h-3.5" />
                Nuova
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* FILTRI */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <select className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700"
              value={filtroTipologia} onChange={(e) => setFiltroTipologia(e.target.value)}>
              <option value="">Tutte le tipologie</option>
              {tipologie.map(t => (<option key={t.id} value={t.id}>{t.nome}</option>))}
            </select>
            <Input type="date" className="h-8 text-xs w-36" value={filtroDataDa} onChange={(e) => setFiltroDataDa(e.target.value)} placeholder="Da" />
            <Input type="date" className="h-8 text-xs w-36" value={filtroDataA} onChange={(e) => setFiltroDataA(e.target.value)} placeholder="A" />
            {(filtroTipologia || filtroDataDa || filtroDataA) && (
              <button onClick={() => { setFiltroTipologia(''); setFiltroDataDa(''); setFiltroDataA(''); }}
                className="h-8 px-2 text-xs text-gray-500 hover:text-gray-700 underline">Azzera</button>
            )}
            <div className="ml-auto flex gap-1.5">
              <Button size="sm" variant="outline" onClick={esportaPDF}
                className="h-8 gap-1 text-xs text-gray-500 border-gray-200 hover:text-red-600 hover:bg-red-50">
                <FileText className="w-3.5 h-3.5" /> PDF
              </Button>
              <Button size="sm" variant="outline" onClick={esportaExcel}
                className="h-8 gap-1 text-xs text-gray-500 border-gray-200 hover:text-emerald-600 hover:bg-emerald-50">
                <FileText className="w-3.5 h-3.5" /> Excel
              </Button>
            </div>
          </div>

          {comunicazioni.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Nessuna comunicazione</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4"
                onClick={() => {
                  resetComunicazioneForm();
                  setShowComunicazioneDialog(true);
                }}
              >
                Crea prima
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {comunicazioni
                .filter(com => {
                  // Filtro tipologia
                  if (filtroTipologia && com.tipologia_id !== filtroTipologia) {
                    return false;
                  }
                  
                  // Filtro data
                  const comData = new Date(com.created_at);
                  if (filtroDataDa) {
                    const dataDa = new Date(filtroDataDa);
                    if (comData < dataDa) return false;
                  }
                  if (filtroDataA) {
                    const dataA = new Date(filtroDataA);
                    dataA.setHours(23, 59, 59, 999);
                    if (comData > dataA) return false;
                  }
                  
                  return true;
                })
                .map(com => {
                const lettureCount = com.letture?.length || 0;
                return (
                  <div 
                    key={com.id} 
                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 bg-white transition-all group"
                  >
                    <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 bg-cyan-100">
                      <MessageSquare className="w-4 h-4 text-cyan-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-sm text-gray-900">{com.titolo}</h3>
                        {com.tipologia_nome && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{com.tipologia_nome}</span>
                        )}
                        {!com.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">Disattivata</span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                        {com.protocollo && (
                          <span className="font-mono text-cyan-600">{com.protocollo}</span>
                        )}
                        <span>{new Date(com.created_at).toLocaleDateString('it-IT')}</span>
                        <span className="text-cyan-600 font-medium">{lettureCount} letture</span>
                        
                        {!com.ruoli_abilitati?.length && !com.profili_abilitati?.length && !com.asl_abilitate?.length && (
                          <span className="text-emerald-600">Tutti</span>
                        )}
                        {(com.ruoli_abilitati?.length > 0 || com.profili_abilitati?.length > 0 || com.asl_abilitate?.length > 0) && (
                          <span className="text-gray-400">
                            {com.ruoli_abilitati?.length > 0 && `Ruoli: ${com.ruoli_abilitati.join(', ')}`}
                            {com.profili_abilitati?.length > 0 && ` / Profili: ${com.profili_abilitati.join(', ')}`}
                            {com.asl_abilitate?.length > 0 && ` / ASL: ${com.asl_abilitate.join(', ')}`}
                          </span>
                        )}
                        
                        {com.link_esterno && (
                          <span className="text-cyan-600 truncate max-w-[200px]"><LinkIcon className="w-3 h-3 inline mr-0.5" />{com.link_esterno}</span>
                        )}
                        {com.file_pdf_nome && (
                          <span className="text-red-500"><FileText className="w-3 h-3 inline mr-0.5" />{com.file_pdf_nome}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-0.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50"
                        onClick={() => { setSelectedItem(com); setShowViewComunicazioneDialog(true); }} title="Visualizza">
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-violet-600 hover:bg-violet-50"
                        onClick={() => openTrackingDialog(com)} title="Tracking letture">
                        <Bell className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                          setSelectedItem(com);
                          setComunicazioneForm({
                            tipo: com.tipo,
                            tipologia_id: com.tipologia_id || '',
                            titolo: com.titolo,
                            contenuto: com.contenuto,
                            link_esterno: com.link_esterno || '',
                            ruoli_abilitati: com.ruoli_abilitati || [],
                            profili_abilitati: com.profili_abilitati || [],
                            asl_abilitate: com.asl_abilitate || [],
                            combinazione_filtri: com.combinazione_filtri || 'OR'
                          });
                          setShowComunicazioneDialog(true);
                        }} title="Modifica">
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteComunicazione(com.id)} title="Elimina">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SEZIONE PRIVACY POLICY */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="w-4 h-4 text-cyan-600" />
                Informative Privacy
              </CardTitle>
              <CardDescription className="text-xs">Per registrazione utenti (GDPR)</CardDescription>
            </div>
            <Button 
              onClick={() => setShowPolicyDialog(true)}
              size="sm"
              className="h-8 gap-1.5 text-xs bg-cyan-600 hover:bg-cyan-700"
            >
              <Plus className="w-3.5 h-3.5" />
              Nuova Informativa
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activePolicy && (
            <div className="mb-4 p-3 border border-emerald-200 rounded-lg bg-emerald-50/50">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <div>
                    <h3 className="font-medium text-sm text-gray-900">{activePolicy.title} <span className="text-xs text-gray-500">v{activePolicy.version}</span></h3>
                    <p className="text-xs text-gray-500">Attiva dal {new Date(activePolicy.effective_date).toLocaleDateString('it-IT')}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50"
                  onClick={() => { setSelectedItem(activePolicy); setShowViewPolicyDialog(true); }}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            {policies.map(policy => (
              <div 
                key={policy.id} 
                className={`flex justify-between items-center p-3 rounded-lg border ${
                  policy.is_active ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200'
                }`}
              >
                <div>
                  <h4 className="font-medium text-sm text-gray-900">{policy.title} <span className="text-xs text-gray-500">v{policy.version}</span></h4>
                  <p className="text-xs text-gray-500">{new Date(policy.created_at).toLocaleDateString('it-IT')}</p>
                </div>
                <div className="flex gap-0.5">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50" onClick={() => {
                    setSelectedItem(policy);
                    setShowViewPolicyDialog(true);
                  }}>
                    <Eye className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-blue-600" onClick={() => fetchLetturePolicy(policy.id)} title="Visualizza letture">
                    <Users className="w-3 h-3" />
                    <span className="ml-1 text-xs">{policy.letture?.length || 0}</span>
                  </Button>
                  {!policy.is_active && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => {
                        setSelectedItem(policy);
                        setPolicyForm({
                          version: policy.version,
                          title: policy.title,
                          content: policy.content,
                          effective_date: policy.effective_date?.split('T')[0] || new Date().toISOString().split('T')[0]
                        });
                        setShowEditPolicyDialog(true);
                      }}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleActivatePolicy(policy.id)}>
                        Attiva
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeletePolicy(policy.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  {policy.is_active && (
                    <Button size="sm" variant="outline" onClick={() => {
                      setSelectedItem(policy);
                      setPolicyForm({
                        version: policy.version,
                        title: policy.title,
                        content: policy.content,
                        effective_date: policy.effective_date?.split('T')[0] || new Date().toISOString().split('T')[0]
                      });
                      setShowEditPolicyDialog(true);
                    }}>
                      <Edit className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 border-t">
            <h4 className="font-semibold mb-2">Consensi Raccolti: {consents.length}</h4>
            {consents.length > 0 && (
              <div className="text-xs text-gray-600">
                Ultimo: {consents[0]?.first_name} {consents[0]?.last_name} - {new Date(consents[0]?.privacy_consent_date).toLocaleDateString('it-IT')}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SEZIONE STATISTICHE LETTURE GLOBALI */}
      {statisticheLettureGlobali && (
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-50">
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Statistiche Letture Globali
            </CardTitle>
            <CardDescription>Panoramica generale delle letture di comunicazioni e informative</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Totale Comunicazioni */}
              <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-blue-900">{statisticheLettureGlobali.comunicazioni.totale}</p>
                    <p className="text-xs text-blue-700">Comunicazioni Totali</p>
                  </div>
                </div>
              </div>
              
              {/* Comunicazioni Lette */}
              <div className="p-4 border-2 border-green-200 rounded-lg bg-green-50">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-2xl font-bold text-green-900">{statisticheLettureGlobali.comunicazioni.totale_letture}</p>
                    <p className="text-xs text-green-700">Letture Totali</p>
                  </div>
                </div>
              </div>
              
              {/* Media Letture */}
              <div className="p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <div>
                    <p className="text-2xl font-bold text-orange-900">{statisticheLettureGlobali.comunicazioni.media_letture}</p>
                    <p className="text-xs text-orange-700">Media Letture/Com.</p>
                  </div>
                </div>
              </div>
              
              {/* Totale Operatori */}
              <div className="p-4 border-2 border-cyan-200 rounded-lg bg-cyan-50">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <div>
                    <p className="text-2xl font-bold text-cyan-900">{statisticheLettureGlobali.operatori.totale}</p>
                    <p className="text-xs text-cyan-700">Operatori Totali</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Privacy Stats */}
            {statisticheLettureGlobali.privacy.totale > 0 && (
              <div className="mt-4 p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
                <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Informative Privacy
                </h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xl font-bold text-purple-900">{statisticheLettureGlobali.privacy.totale}</p>
                    <p className="text-xs text-purple-700">Policy Attive</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-purple-900">{statisticheLettureGlobali.privacy.totale_letture}</p>
                    <p className="text-xs text-purple-700">Letture Totali</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-purple-900">{consents.length}</p>
                    <p className="text-xs text-purple-700">Consensi Raccolti</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* DIALOG COMUNICAZIONE */}
      <Dialog open={showComunicazioneDialog} onOpenChange={setShowComunicazioneDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedItem ? 'Modifica' : 'Nuova'} Comunicazione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitComunicazione}>
            <div className="space-y-4">
              {/* INFO PROTOCOLLO */}
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                <p className="text-sm text-cyan-900 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <strong>Protocollo Automatico:</strong> Verrà assegnato un numero protocollo progressivo (es: PROT-2025-001) alla creazione
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipologia *</Label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={comunicazioneForm.tipologia_id}
                    onChange={(e) => setComunicazioneForm({...comunicazioneForm, tipologia_id: e.target.value})}
                    required
                  >
                    <option value="">Seleziona tipologia</option>
                    {tipologie.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.emoji} {t.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Titolo *</Label>
                  <Input
                    required
                    value={comunicazioneForm.titolo}
                    onChange={(e) => setComunicazioneForm({...comunicazioneForm, titolo: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Contenuto (HTML supportato) *</Label>
                <Textarea
                  required
                  rows={10}
                  value={comunicazioneForm.contenuto}
                  onChange={(e) => setComunicazioneForm({...comunicazioneForm, contenuto: e.target.value})}
                  className="font-mono text-sm"
                />
              </div>
              
              <div>
                <Label>Link Esterno (opzionale)</Label>
                <Input
                  type="url"
                  value={comunicazioneForm.link_esterno}
                  onChange={(e) => setComunicazioneForm({...comunicazioneForm, link_esterno: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <Label>Allega PDF (opzionale)</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                />
              </div>
              
              <div className="border-2 border-blue-200 rounded p-4 space-y-3">
                <h4 className="font-semibold">Visibilità</h4>
                
                <div>
                  <Label>Ruoli</Label>
                  <div className="flex gap-3 mt-2">
                    {['operatore', 'coordinatore'].map(r => (
                      <label key={r} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={comunicazioneForm.ruoli_abilitati.includes(r)}
                          onChange={() => toggleArray(
                            comunicazioneForm.ruoli_abilitati, 
                            r, 
                            (val) => setComunicazioneForm({...comunicazioneForm, ruoli_abilitati: val})
                          )}
                        />
                        {r}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Profili</Label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {settings?.figure_professionali?.map(f => (
                      <label key={f.nome} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={comunicazioneForm.profili_abilitati.includes(f.nome)}
                          onChange={() => toggleArray(
                            comunicazioneForm.profili_abilitati, 
                            f.nome, 
                            (val) => setComunicazioneForm({...comunicazioneForm, profili_abilitati: val})
                          )}
                        />
                        {f.nome}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>ASL di Riferimento</Label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {['Asl Pescara', 'Asl Chieti', "Asl L'Aquila", 'Asl Teramo'].map(asl => (
                      <label key={asl} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={comunicazioneForm.asl_abilitate.includes(asl)}
                          onChange={() => toggleArray(
                            comunicazioneForm.asl_abilitate, 
                            asl, 
                            (val) => setComunicazioneForm({...comunicazioneForm, asl_abilitate: val})
                          )}
                        />
                        {asl}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Combinazione Filtri</Label>
                  <select
                    className="w-full border rounded px-3 py-2 mt-2"
                    value={comunicazioneForm.combinazione_filtri}
                    onChange={(e) => setComunicazioneForm({...comunicazioneForm, combinazione_filtri: e.target.value})}
                  >
                    <option value="OR">OR (almeno uno)</option>
                    <option value="AND">AND (tutti insieme)</option>
                  </select>
                  <p className="text-xs text-gray-600 mt-1">
                    {comunicazioneForm.combinazione_filtri === 'OR' ? 
                      'Visibile se utente corrisponde ad ALMENO UNO dei filtri' : 
                      'Visibile se utente corrisponde a TUTTI i filtri contemporaneamente'
                    }
                  </p>
                </div>
              </div>
            </div>
            
            {/* CONTROLLO NOTIFICHE EMAIL */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={comunicazioneForm.invia_notifiche}
                  onChange={(e) => setComunicazioneForm({...comunicazioneForm, invia_notifiche: e.target.checked})}
                  className="mt-1 w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-yellow-900">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Invia notifiche email agli operatori
                  </div>
                  <p className="text-xs text-yellow-700 mt-1">
                    {comunicazioneForm.invia_notifiche ? 
                      '✅ Le email verranno inviate a tutti gli operatori destinatari' : 
                      '⚠️ Nessuna email verrà inviata (comunicazione salvata senza notifica)'
                    }
                  </p>
                </div>
              </label>
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => {
                setShowComunicazioneDialog(false);
                resetComunicazioneForm();
              }}>
                Annulla
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIALOG TRACKING */}
      {showTrackingDialog && trackingData && (
        <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Tracking Letture - {trackingData.titolo}</DialogTitle>
              <DialogDescription>
                {trackingData.totale_letture} letture totali
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {trackingData.letture.length === 0 ? (
                <p className="text-center py-8 text-gray-500">Nessuna lettura</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Utente</th>
                      <th className="text-left py-2">Username</th>
                      <th className="text-left py-2">Data Lettura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingData.letture.map((l) => (
                      <tr key={`${l.username}-${l.data_lettura}`} className="border-b">
                        <td className="py-2">{l.full_name}</td>
                        <td className="py-2">@{l.username}</td>
                        <td className="py-2">{new Date(l.data_lettura).toLocaleString('it-IT')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setShowTrackingDialog(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* DIALOG VIEW COMUNICAZIONE */}
      {showViewComunicazioneDialog && selectedItem && (
        <Dialog open={showViewComunicazioneDialog} onOpenChange={setShowViewComunicazioneDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedItem.titolo}</DialogTitle>
              <DialogDescription>
                Creata il {new Date(selectedItem.created_at).toLocaleDateString('it-IT')}
              </DialogDescription>
            </DialogHeader>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: selectedItem.contenuto }}
            />
            {selectedItem.link_esterno && (
              <div className="mt-4">
                <a href={selectedItem.link_esterno} target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline">
                  {selectedItem.link_esterno}
                </a>
              </div>
            )}
            {selectedItem.file_pdf_url && (
              <div className="mt-4">
                <a href={getAuthenticatedFileUrl(selectedItem.file_pdf_url)} target="_blank" rel="noopener noreferrer" className="text-red-600 underline">
                  📄 {selectedItem.file_pdf_nome}
                </a>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setShowViewComunicazioneDialog(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* DIALOG CREATE/EDIT POLICY */}
      <Dialog open={showPolicyDialog || showEditPolicyDialog} onOpenChange={(open) => {
        if (!open) {
          setShowPolicyDialog(false);
          setShowEditPolicyDialog(false);
          setSelectedItem(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditPolicyDialog ? 'Modifica' : 'Nuova'} Informativa Privacy</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitPolicy}>
            <div className="space-y-4">
              {!showEditPolicyDialog && (
                <div>
                  <Label>Versione *</Label>
                  <Input
                    required
                    value={policyForm.version}
                    onChange={(e) => setPolicyForm({...policyForm, version: e.target.value})}
                    placeholder="1.0"
                  />
                </div>
              )}
              
              <div>
                <Label>Titolo</Label>
                <Input
                  value={policyForm.title}
                  onChange={(e) => setPolicyForm({...policyForm, title: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Contenuto (HTML) *</Label>
                <Textarea
                  required
                  rows={15}
                  value={policyForm.content}
                  onChange={(e) => setPolicyForm({...policyForm, content: e.target.value})}
                  className="font-mono text-sm"
                />
              </div>
              
              {!showEditPolicyDialog && (
                <div>
                  <Label>Data Effettiva</Label>
                  <Input
                    type="date"
                    value={policyForm.effective_date}
                    onChange={(e) => setPolicyForm({...policyForm, effective_date: e.target.value})}
                  />
                </div>
              )}
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => {
                setShowPolicyDialog(false);
                setShowEditPolicyDialog(false);
                setSelectedItem(null);
              }}>
                Annulla
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIALOG VIEW POLICY */}
      {showViewPolicyDialog && selectedItem && (
        <Dialog open={showViewPolicyDialog} onOpenChange={setShowViewPolicyDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedItem.title} - v{selectedItem.version}</DialogTitle>
              <DialogDescription>
                {new Date(selectedItem.created_at).toLocaleDateString('it-IT')}
              </DialogDescription>
            </DialogHeader>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: selectedItem.content }}
            />
            <DialogFooter>
              <Button onClick={() => setShowViewPolicyDialog(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* DIALOG LETTURE PRIVACY POLICY */}
      {lettureData && (
        <Dialog open={showLettureDialog} onOpenChange={setShowLettureDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Letture Informativa Privacy
              </DialogTitle>
              <DialogDescription>
                {lettureData.policy.title} - v{lettureData.policy.version}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Statistiche */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-blue-50 border-2 border-blue-200 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-900">{lettureData.totale_letture}</p>
                  <p className="text-xs text-blue-700">Letture Totali</p>
                </div>
                <div className="p-3 bg-green-50 border-2 border-green-200 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-900">{lettureData.totale_operatori}</p>
                  <p className="text-xs text-green-700">Operatori Totali</p>
                </div>
                <div className="p-3 bg-purple-50 border-2 border-purple-200 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-900">
                    {lettureData.totale_operatori > 0 
                      ? Math.round((lettureData.totale_letture / lettureData.totale_operatori) * 100) 
                      : 0}%
                  </p>
                  <p className="text-xs text-purple-700">Copertura</p>
                </div>
              </div>
              
              {/* Tabella Letture */}
              <div>
                <h4 className="font-semibold mb-2">Dettaglio Letture ({lettureData.letture.length})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="text-left p-2">Operatore</th>
                        <th className="text-left p-2">Username</th>
                        <th className="text-left p-2">Data Lettura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lettureData.letture.length > 0 ? (
                        lettureData.letture.map((lettura) => (
                          <tr key={`${lettura.username}-${lettura.data_lettura}`} className="border-b hover:bg-gray-50">
                            <td className="p-2">{lettura.full_name}</td>
                            <td className="p-2 text-gray-600">{lettura.username}</td>
                            <td className="p-2 text-gray-600">
                              {new Date(lettura.data_lettura).toLocaleString('it-IT')}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="p-4 text-center text-gray-500">
                            Nessuna lettura registrata
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button onClick={() => setShowLettureDialog(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* DIALOG GESTIONE TIPOLOGIE */}
      <Dialog open={showTipologieDialog} onOpenChange={setShowTipologieDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestione Tipologie Comunicazioni</DialogTitle>
            <DialogDescription>
              Crea e gestisci le tipologie disponibili per le comunicazioni
            </DialogDescription>
          </DialogHeader>
          
          {/* FORM NUOVA TIPOLOGIA */}
          <form onSubmit={handleCreateTipologia} className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
            <h3 className="font-semibold mb-3 text-blue-900">Crea Nuova Tipologia</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input
                  required
                  value={tipologiaForm.nome}
                  onChange={(e) => setTipologiaForm({...tipologiaForm, nome: e.target.value})}
                  placeholder="es: Comunicazione, Circolare..."
                />
              </div>
              <div>
                <Label>Emoji *</Label>
                <Input
                  required
                  value={tipologiaForm.emoji}
                  onChange={(e) => setTipologiaForm({...tipologiaForm, emoji: e.target.value})}
                  placeholder="📣"
                  maxLength={2}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Copia emoji da: <a href="https://emojipedia.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">emojipedia.org</a>
                </p>
              </div>
              <div>
                <Label>Colore *</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={tipologiaForm.colore}
                    onChange={(e) => setTipologiaForm({...tipologiaForm, colore: e.target.value})}
                    className="w-20"
                  />
                  <Input
                    value={tipologiaForm.colore}
                    onChange={(e) => setTipologiaForm({...tipologiaForm, colore: e.target.value})}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>
              <div>
                <Label>Ordine</Label>
                <Input
                  type="number"
                  value={tipologiaForm.ordine}
                  onChange={(e) => setTipologiaForm({...tipologiaForm, ordine: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <Button type="submit" className="mt-3 w-full bg-blue-600" disabled={loading}>
              <Plus className="w-4 h-4 mr-2" />
              {loading ? 'Creazione...' : 'Crea Tipologia'}
            </Button>
          </form>

          {/* LISTA TIPOLOGIE ESISTENTI */}
          <div className="space-y-2 mt-4">
            <h3 className="font-semibold text-gray-900">Tipologie Esistenti</h3>
            {tipologie.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">Nessuna tipologia creata</p>
            ) : (
              <div className="space-y-2">
                {tipologie.map(tip => (
                  <div key={tip.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{tip.emoji}</span>
                      <div>
                        <p className="font-semibold text-gray-900">{tip.nome}</p>
                        <p className="text-xs text-gray-500">Ordine: {tip.ordine}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded border-2"
                        style={{ backgroundColor: tip.colore }}
                        title={tip.colore}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteTipologia(tip.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button onClick={() => setShowTipologieDialog(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
